import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, inr, num, pct, tone, TIMEFRAMES } from "@/lib/api";
import { OhlcLegend, OVERLAYS, PriceChart } from "@/components/PriceChart";

const QuickOrder = ({ symbol, price, onTraded }) => {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const place = async (side) => {
    setBusy(true);
    try {
      const { data } = await api.post("/paper/order", { symbol, side, qty: Number(qty) });
      toast.success(`${side} ${data.qty} ${symbol} @ ${inr(data.price)}`);
      onTraded();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="panel p-3 flex flex-wrap items-end gap-3" data-testid="quick-order">
      <div>
        <div className="widget-title mb-1">Quick Paper Order · Market</div>
        <div className="text-xs text-slate-400">Est. value <span className="font-mono text-slate-200">{inr((price || 0) * qty)}</span></div>
      </div>
      <input data-testid="quick-order-qty" type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} className="field w-24" />
      <button data-testid="quick-order-buy" disabled={busy} onClick={() => place("BUY")} className="btn-bull">BUY</button>
      <button data-testid="quick-order-sell" disabled={busy} onClick={() => place("SELL")} className="btn-bear">SELL</button>
    </div>
  );
};

export const ChartTab = ({ symbol, quotes, onTraded }) => {
  const [tf, setTf] = useState("5m");
  const [ind, setInd] = useState({ ema9: true, ema21: true, sma50: false, sma200: false, bb: false, rsi: true, macd: true });
  const [last, setLast] = useState(null);
  const q = quotes.find((x) => x.symbol === symbol);
  return (
    <div className="flex flex-col gap-3 rise">
      <div className="panel">
        <div className="flex flex-wrap items-center gap-4 px-3 py-2 border-b border-[#163056]">
          <div>
            <h1 className="font-display text-xl font-bold text-white tracking-tight" data-testid="chart-symbol">{symbol}</h1>
            <div className="text-[10px] text-slate-500">{q?.name} · NSE</div>
          </div>
          {q && (
            <div className="font-mono">
              <span className="text-lg text-white" data-testid="chart-ltp">{num(q.price)}</span>{" "}
              <span className={`text-xs ${tone(q.change)}`}>{num(q.change)} ({pct(q.change_pct)})</span>
            </div>
          )}
          <OhlcLegend last={last} />
          <div className="flex gap-1 ml-auto" data-testid="timeframe-selector">
            {TIMEFRAMES.map((t) => (
              <button key={t} data-testid={`tf-${t}`} onClick={() => setTf(t)}
                className={`px-2 h-7 text-[11px] font-mono rounded-sm border transition-colors ${tf === t ? "border-[#22A34A] text-[#22A34A] bg-[#22A34A]/5" : "border-[#163056] text-slate-400 hover:text-white"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-[#163056]" data-testid="indicator-toggles">
          {OVERLAYS.map((o) => (
            <button key={o.key} data-testid={`ind-toggle-${o.key}`} onClick={() => setInd((p) => ({ ...p, [o.key]: !p[o.key] }))}
              className={`flex items-center gap-1.5 px-2 h-6 text-[10px] font-mono rounded-sm border transition-colors ${ind[o.key] ? "border-[#334155] text-slate-100 bg-[#102A4A]" : "border-[#163056] text-slate-500"}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: ind[o.key] ? o.color : "#334155" }} /> {o.label}
            </button>
          ))}
        </div>
        <PriceChart symbol={symbol} tf={tf} ind={ind} onLast={setLast} />
      </div>
      <QuickOrder symbol={symbol} price={q?.price} onTraded={onTraded} />
    </div>
  );
};
